import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { parseTimeValue, getCommonTextFieldStyles } from '../../_utils/fieldInputHelpers';
import { translate as t } from '../../../../../../hooks/useTranslations';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface BaseTimeInputProps {
  value: Dayjs | string | null;
  onChange: (value: Dayjs | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  showTimezone?: boolean;
}

const BaseTimeInput: React.FC<BaseTimeInputProps> = ({
  value,
  onChange,
  label = 'Select time',
  required = false,
  disabled = false,
  error,
  helperText,
  size = 'small',
  fullWidth = true,
  showTimezone = true,
}) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [pickerError, setPickerError] = useState<string | null>(null);

  // Parse the value like the original TimeField - handle both UTC timestamps and Dayjs objects
  const timeValue = dayjs.isDayjs(value) ? value : value ? dayjs.utc(value).local() : null;

  const handleChange = (time: Dayjs | null) => {
    // Store the Dayjs object directly - let the form submission handle UTC conversion
    // This matches the original TimeField behavior
    onChange(time ?? null);
  };

  const handleError = (reason: any) => {
    setPickerError(reason ? 'Invalid time' : null);
  };

  // Combine form validation error and picker error (prioritize form error)
  const showError = !!error || !!pickerError;
  const displayHelperText = error || pickerError || helperText;

  return (
    <Box>
      <TimePicker
        label={label}
        value={timeValue}
        onChange={handleChange}
        onError={handleError}
        format='HH:mm'
        ampm={false}
        timeSteps={{ hours: 1, minutes: 1 }}
        views={['hours', 'minutes']}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth,
            size,
            error: showError,
            helperText: displayHelperText,
            placeholder: 'HH:MM (24-hour format)',
            required,
            sx: getCommonTextFieldStyles(),
            onPaste: (e: React.ClipboardEvent) => e.preventDefault(),
          },
          layout: {
            sx: {
              '& .MuiMultiSectionDigitalClockSection-root::before, & .MuiMultiSectionDigitalClockSection-root::after': {
                display: 'none',
              },
              '& .MuiMultiSectionDigitalClockSection-root': {
                overscrollBehavior: 'contain',
              },
            },
          },
          digitalClockSectionItem: { sx: { fontSize: '0.875rem' } },
        }}
      />

      {showTimezone && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mt: 0.5, display: 'block' }}
        >
          {t('performanceReporting.fieldBank.form.timezoneNote').replace('{{timezone}}', userTimezone)}
        </Typography>
      )}
    </Box>
  );
};

export default BaseTimeInput;
